import type { Store } from '@libs/common-node';
import { format, parse, compareAsc } from 'date-fns';

const defaultOpenTime = '09:00:00';
const defaultCloseTime = '21:00:00';

const getTodaysHours = (store: Store, day: number) =>
    (store.StandardStoreHours ?? store.CurrentStoreHours ?? []).find(
        d => d.DayOfWeek === day
    ) ?? null;

export const getClosingTime = (
    store: Store,
    currentDate: Date,
    day: number
): Date => {
    const today = getTodaysHours(store, day);

    if (today && today.CloseTime) {
        return parse(today.CloseTime, 'HH:mm:ss', currentDate);
    }

    return parse(defaultCloseTime, 'HH:mm:ss', currentDate);
};

export const getOpeningTime = (
    store: Store,
    currentDate: Date,
    day: number
): Date => {
    const today = getTodaysHours(store, day);

    if (today && today.OpenTime) {
        return parse(today.OpenTime, 'HH:mm:ss', currentDate);
    }

    return parse(defaultOpenTime, 'HH:mm:ss', currentDate);
};

export const getOpeningTimeToday = (store: Store, currentDate: Date) =>
    getOpeningTime(store, currentDate, currentDate.getDay());

export const getClosingTimeToday = (store: Store, currentDate: Date) =>
    getClosingTime(store, currentDate, currentDate.getDay());

export const formatToDisplayHours = (date: Date) => format(date, 'haaa');

export const storeIsOpen = (store: Store, currentDate: Date): boolean => {
    const openingHour = getOpeningTimeToday(store, currentDate);
    const closingHour = getClosingTimeToday(store, currentDate);

    if (openingHour && closingHour) {
        // currentDates datetime must be past opening hour and before closing hour
        return (
            compareAsc(currentDate, openingHour) === 1 &&
            compareAsc(currentDate, closingHour) === -1
        );
    }

    return false;
};

export const getStoreDisplayTime = (
    store: Store,
    currentDate: Date
): string => {
    if (storeIsOpen(store, currentDate)) {
        return `Open until ${formatToDisplayHours(
            getClosingTimeToday(store, currentDate)
        )}`;
    }

    return `Opens at ${formatToDisplayHours(
        getOpeningTimeToday(store, currentDate)
    )}`;
};

export const dayOfWeekAsString = (dayIndex: number) =>
    ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'][dayIndex] || '';

export const dayOfWeekFullNameAsString = (dayIndex: number) =>
    [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
    ][dayIndex] || '';

export const changeTimeFormat = (storeHour: string): string => {
    const hour24 = parseInt(storeHour.slice(0, 2), 10);

    if (isNaN(hour24) || hour24 < 0 || hour24 > 23) {
        throw new Error(`Invalid hour: "${storeHour}"`);
    }

    const hour12 = hour24 % 12 || 12;
    const period = hour24 >= 12 ? 'pm' : 'am';

    return `${hour12}${period}`;
};
